import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { Search, Info, ShoppingCart } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService } from '../services/apiService';

const PartSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery) {
      toast.warning('Please enter a part number');
      return;
    }

    setSearching(true);
    try {
      const response = await apiService.searchParts({
        parts: [{
          part_number: searchQuery,
          manufacturer: manufacturer,
          quantity: quantity
        }],
        search_type: 'exact'
      });

      if (response.results && response.results.length > 0) {
        setResults(response.results[0]);
      } else {
        setResults({ distributors: [] });
        toast.info('No results found for this part');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search for part');
    } finally {
      setSearching(false);
    }
  };

  const formatPrice = (price) => {
    return typeof price === 'number' ? `$${price.toFixed(4)}` : '-';
  };

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Part Search</h2>
          <p className="text-muted">Search for individual components across multiple distributors</p>
        </Col>
      </Row>

      <Row>
        <Col lg={4}>
          <Card>
            <Card.Body>
              <h5>Search Parameters</h5>
              <Form onSubmit={handleSearch}>
                <Form.Group className="mb-3">
                  <Form.Label>Part Number *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., SN74HC595N"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Manufacturer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Texas Instruments"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  />
                </Form.Group>

                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-100"
                  disabled={searching}
                >
                  {searching ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="me-2" />
                      Search Part
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Body>
              <h6>Search Tips</h6>
              <ul className="small mb-0">
                <li>Enter exact manufacturer part numbers for best results</li>
                <li>Manufacturer field helps filter results</li>
                <li>Quantity affects volume pricing calculations</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          {results && (
            <Card>
              <Card.Header>
                <h5>
                  {searchQuery}
                  {manufacturer && <span className="text-muted"> - {manufacturer}</span>}
                </h5>
              </Card.Header>
              <Card.Body>
                {results.distributors && results.distributors.length > 0 ? (
                  <>
                    <h6>Distributor Pricing & Availability</h6>
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>Distributor</th>
                          <th>SKU</th>
                          <th>Stock</th>
                          <th>MOQ</th>
                          <th>Unit Price</th>
                          <th>Extended</th>
                          <th>Lead Time</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.distributors.map((dist, index) => (
                          <tr key={index}>
                            <td>{dist.distributor}</td>
                            <td>{dist.sku}</td>
                            <td>
                              <Badge bg={dist.stock > 0 ? 'success' : 'danger'}>
                                {dist.stock}
                              </Badge>
                            </td>
                            <td>{dist.moq}</td>
                            <td>{formatPrice(dist.unit_price)}</td>
                            <td><strong>{formatPrice(dist.extended_price)}</strong></td>
                            <td>{dist.lead_time}</td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="outline-primary"
                                href={dist.product_url}
                                target="_blank"
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {results.distributors[0]?.price_breaks && (
                      <>
                        <h6 className="mt-4">Price Breaks</h6>
                        <Table size="sm" className="w-auto">
                          <thead>
                            <tr>
                              <th>Quantity</th>
                              <th>Unit Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.distributors[0].price_breaks.map((pb, index) => (
                              <tr key={index}>
                                <td>{pb.quantity}+</td>
                                <td>{formatPrice(pb.price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}

                    {results.alternative_parts && results.alternative_parts.length > 0 && (
                      <>
                        <h6 className="mt-4">Alternative Parts</h6>
                        <Table size="sm">
                          <thead>
                            <tr>
                              <th>Part Number</th>
                              <th>Manufacturer</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.alternative_parts.map((alt, index) => (
                              <tr key={index}>
                                <td>{alt.mpn}</td>
                                <td>{alt.manufacturer}</td>
                                <td>{alt.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Info style={{ fontSize: 48, color: 'var(--bw-muted)' }} />
                    <p className="mt-3">No distributor data available for this part</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default PartSearch;